import { Percent } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, DialogTitle } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
	ContactType,
	useCreateContractedInformationMutation,
	useGetAllCompaniesLazyQuery,
	useUpdateContractedInformationMutation,
	Contact,
} from "@repo/server-api";
import { ContractedInformation, OptionType } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState } from "react";

import { setContactValues, validErrorState } from "@repo/legacy/components/helper/Index";

import DialogContact from "./DialogContact";
import NumberTextField from "../fields/NumberTextField";
import Dropdown from "../sections/Dropdown";

export default function DialogContractedCompany(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: (company: ContractedInformation) => void;
		company: ContractedInformation | null;
		setCompany: (value: ContractedInformation | null) => void;
		contactId: string;
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [company, setCompany] = useState<ContractedInformation>({
		id: "",
		contractedId: "",
		marge: 0,
	} as ContractedInformation);

	const [elements, setElements] = useState<Contact[]>([]);
	const [element, setElement] = useState<OptionType | null>(null);
	const [openDialogCompany, setOpenDialogCompany] = useState<boolean>(false);
	const [newCompany, setNewCompany] = useState<Contact | null>(null);

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const [isLoaded, setIsLoaded] = useState<boolean>(false);

	const [updateContractedCompany] = useUpdateContractedInformationMutation({
		variables: {
			contactId: props.contactId,
			contractedId: company?.contractedId ?? "",
			margin: company?.marge ?? 0,
			contactInformationId: company?.id ?? "",
		},
		onCompleted(data) {
			const c = data.updateContractedInformation.contractedInformation;
			if (c) {
				(c as ContractedInformation).displayName = company?.displayName ?? "";
				(c as ContractedInformation).key = Math.random() * 1000;
				props.refreshData(c);
				handleClose();
			}
		},
	});

	const [createContractedCompany] = useCreateContractedInformationMutation({
		variables: {
			contactId: props.contactId,
			margin: company?.marge ?? 0,
			contractedId: company?.contractedId ?? "",
		},
		onCompleted(data) {
			const c = data.createContractedInformation.contractedInformation;
			if (c) {
				(c as ContractedInformation).displayName = company?.displayName ?? "";
				(c as ContractedInformation).key = Math.random() * 1000;
				props.refreshData(c);
				handleClose();
			}
		},
	});

	const [getAllCompanies] = useGetAllCompaniesLazyQuery({
		fetchPolicy: "no-cache",
		variables: { first: 100, after: null },
		onCompleted(data) {
			if (data && data.allContacts?.nodes) {
				const contacts = data.allContacts.nodes.map((contact) => {
					if (contact.id === company?.contractedId) setNewCompany(contact as unknown as Contact);
					return {
						...setContactValues(contact as unknown as Contact),
					};
				});
				setElements([...contacts]);
			}
		},
	});

	useEffect(() => {
		if (element?.item && isLoaded) {
			setCompany({
				...company,
				displayName: (element.item as Contact).displayName,
				contractedId: element.item.id,
			});
		}
	}, [element]);

	useEffect(() => {
		if (newCompany) {
			setElement({
				item: newCompany,
				inputValue: newCompany.displayName,
				title: newCompany.displayName,
			});
		}
	}, [newCompany]);

	useEffect(() => {
		setOpen(props.open);
		if (props.company && !isLoaded) {
			setIsLoaded(true);
			setCompany({
				...props.company,
			});
			getAllCompanies();
		}
	}, [props.company, props.open]);

	const margeRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		localErrors.marge = (await margeRef.current?.validate()) || {};

		// TODO Make this better
		localErrors.company = {
			id: company.contractedId ? "" : t("validation.company.required"),
		};

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleClose = () => {
		setOpen(false);
		setErrors({});
		setCompany({
			id: "",
			contractedId: "",
			marge: 0,
		});
		props.setCompany(null);
		setNewCompany(null);
		setElement(null);
		props.setOpen(false);
		setSaveClicked(false);
		setIsLoaded(false);
	};

	const handleSubmit = async () => {
		const isValid = await validateFields();
		if (isValid) {
			setSaveClicked(true);
			if (company?.id) {
				updateContractedCompany();
			} else {
				createContractedCompany();
			}
			return;
		}
	};

	return (
		<Dialog open={open} maxWidth="sm" fullWidth={true}>
			<DialogTitle>
				{t("contacting-company") +
					" " +
					(props.company?.displayName ? props.company.displayName : "")}
			</DialogTitle>
			<DialogContent className="flex-col gap-5">
				<Box className="fullCenter">
					<Dropdown
						searchLabel={t("company-name")}
						searchPlaceholder={t("company-name")}
						element={element}
						setElement={(value) => {
							setElement(value);
							setErrors({ ...errors, company: { id: "" } });
						}}
						error={
							typeof errors.company === "object" &&
							errors.company !== null &&
							"id" in errors.company
								? ((errors.company as { id?: string }).id as string)
								: ""
						}
						setError={(value) => setErrors({ ...errors, company: { id: value } })}
						elements={elements ?? []}
						noData={t("no-companies-found")}
						doNotShowMenu={true}>
						<DialogContact
							doNotShowClient={true}
							open={openDialogCompany}
							setOpen={setOpenDialogCompany}
							refreshData={() => {
								getAllCompanies();
							}}
							contact={newCompany}
							type={ContactType.Company}
						/>
					</Dropdown>
				</Box>
				<Box className="fullCenter">
					<NumberTextField
						isLoaded={true}
						ref={margeRef}
						label={t("margin")}
						value={company?.marge ?? 0}
						setValue={(value) => {
							setCompany({ ...company, marge: value ?? 0 });
							setErrors({ ...errors, marge: "" });
						}}
						icon={<Percent />}
						integer={true}
						errors={errors.marge as Record<string, string>}
						setErrors={(value) => setErrors({ ...errors, marge: value })}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					variant="contained"
					onClick={handleSubmit}
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
